import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import jakartaLatin from "@fontsource-variable/plus-jakarta-sans/files/plus-jakarta-sans-latin-wght-normal.woff2?url";
import jakartaLatinExt from "@fontsource-variable/plus-jakarta-sans/files/plus-jakarta-sans-latin-ext-wght-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { BottomNav } from "@/components/bottom-nav";

// Kritik olmayan modullar — yalnız səhifə boşalanda (idle) yüklənir
const PwaInstall = lazy(() =>
  import("@/components/pwa-install").then((m) => ({ default: m.PwaInstall })),
);
const PwaSplash = lazy(() =>
  import("@/components/pwa-splash").then((m) => ({ default: m.PwaSplash })),
);
const PullToRefresh = lazy(() =>
  import("@/components/pull-to-refresh").then((m) => ({ default: m.PullToRefresh })),
);
const NotificationsWatcher = lazy(() =>
  import("@/components/notifications-watcher").then((m) => ({ default: m.NotificationsWatcher })),
);
const PresenceHeartbeat = lazy(() =>
  import("@/components/presence-heartbeat").then((m) => ({ default: m.PresenceHeartbeat })),
);

/** Brauzer boş qalanda işə düşən köməkçi */
function onIdle(cb: () => void, timeout = 2500) {
  const w = window as unknown as {
    requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(cb, { timeout });
  else setTimeout(cb, 1200);
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Səhifə tapılmadı</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Axtardığınız səhifə mövcud deyil və ya köçürülüb.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Ana səhifəyə qayıt
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Səhifə yüklənmədi
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Bizim tərəfdə xəta baş verdi. Səhifəni yeniləyin və ya ana səhifəyə qayıdın.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Yenidən cəhd et
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Ana səhifəyə qayıt
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Albazar.az — Azərbaycanda pulsuz elanlar" },
      {
        name: "description",
        content:
          "Albazar.az — Azərbaycanın müasir elan saytı. Ev, maşın, telefon, iş elanları və daha çoxu.",
      },
      { name: "author", content: "Albazar.az" },
      { property: "og:title", content: "Albazar.az — Azərbaycanda pulsuz elanlar" },
      {
        property: "og:description",
        content: "Ev, maşın, telefon, iş elanları — al, sat, kirayələ.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Albazar.az" },
      { property: "og:locale", content: "az_AZ" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#2563EB" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "Albazar.az" },
      { name: "application-name", content: "Albazar.az" },
    ],
    links: [
      // Performans: backend bağlantısını əvvəlcədən qur (sorğu-latency azalır)
      { rel: "preconnect", href: new URL(import.meta.env["VITE_SUPABASE_URL"] as string).origin },
      { rel: "dns-prefetch", href: new URL(import.meta.env["VITE_SUPABASE_URL"] as string).origin },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: jakartaLatin,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: jakartaLatinExt,
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/png", href: "/favicon-v3.png" },
      { rel: "manifest", href: "/manifest.webmanifest?v=3" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon-v3.png" },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="az" suppressHydrationWarning>
      <head>
        <HeadContent />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "try{if(localStorage.getItem('alsat-theme')==='dark'){document.documentElement.classList.add('dark')}}catch(e){}",
          }}
        />
        {/* Quraşdırma hadisəsi səhifə yüklənən kimi tutulur ki, itməsin */}
        <script
          dangerouslySetInnerHTML={{
            __html:
              "window.__albazarInstallListeners=window.__albazarInstallListeners||[];" +
              "window.addEventListener('beforeinstallprompt',function(e){e.preventDefault();window.__albazarInstallPrompt=e;(window.__albazarInstallListeners||[]).forEach(function(c){try{c()}catch(x){}})});" +
              "window.addEventListener('appinstalled',function(){window.__albazarInstalled=true;window.__albazarInstallPrompt=null;(window.__albazarInstallListeners||[]).forEach(function(c){try{c()}catch(x){}})});",
          }}
        />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [router, queryClient]);

  // Kritik olmayan işlər: yalnız brauzer boş qalanda (LCP-dən sonra)
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    onIdle(() => {
      setIdle(true);
      void import("@/lib/pwa").then((m) => m.registerServiceWorker());
      void import("@/lib/pwa-stats").then((m) => m.initPwaTracking());
      void import("@/lib/analytics").then((m) => {
        m.initErrorMonitoring();
        void m.trackVisit(window.location.pathname);
      });
      // Google Analytics — açılışı bloklamır
      const w = window as unknown as { dataLayer?: unknown[] };
      w.dataLayer = w.dataLayer || [];
      function gtag(...args: unknown[]) {
        w.dataLayer!.push(args);
      }
      gtag("js", new Date());
      gtag("config", "G-G634MWS1HP");
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://www.googletagmanager.com/gtag/js?id=G-G634MWS1HP";
      document.head.appendChild(s);
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen w-full max-w-full flex-col overflow-x-hidden pb-[calc(4.5rem+env(safe-area-inset-bottom))] md:pb-0">
        <SiteHeader />
        <main className="flex-1">
          {/* Required: nested routes render here. */}
          <Outlet />
        </main>
        <SiteFooter />
      </div>
      <BottomNav />
      <Toaster />
      <Suspense fallback={null}>
        <PwaSplash />
      </Suspense>
      {idle && (
        <Suspense fallback={null}>
          <PwaInstall />
          <PullToRefresh />
          <NotificationsWatcher />
          <PresenceHeartbeat />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
